/**
 * @fileoverview Choose the media-specific presentation for an already selected document/revision. Images, audio/video, and text share source helpers in `documentSources.js`; text retrieval remains a backend read. This layer owns display selection rather than canonical bytes, preview generation, or editing. Consumers retain source identity and permission context.
 */

import React, { useEffect, useState } from 'react';
import api from '../../api';
import { documentRevisionId } from './documentSources';
import DocumentPreviewImage from './DocumentPreviewImage';
import DocumentFileTypeIcon, { documentTypeLabel } from './DocumentFileTypeIcon';
import './DocumentMedia.css';



export default function DocumentMedia({ document, type, source, thumbnail = false, imageClassName = '', iframeClassName = '' }) {
  const filename = document.filename || document.name || 'Untitled document';
  const revisionId = documentRevisionId(document);
  const [textPreview, setTextPreview] = useState(null);
  const previewText = !thumbnail && !source && revisionId && ['text', 'code', 'csv'].includes(type);
  useEffect(

  () => {
    setTextPreview(null);
    if (!previewText) return undefined;
    let cancelled = false;
    api.get(`/simul/archive/versions/${encodeURIComponent(revisionId)}/download`, { responseType: 'text', transformResponse: [(text) => text] })
      .then(({ data }) => { if (!cancelled) setTextPreview(String(data)); })
      .catch(() => { if (!cancelled) setTextPreview(false); });
    return () => { cancelled = true; };
  }, [previewText, revisionId]);
  if (previewText && textPreview === null) return <div role="status">Loading preview…</div>;
  if (previewText && textPreview !== false) return <pre className="document-file-viewer__text">{textPreview}</pre>;
  if (source && type === 'audio') return <audio controls preload="metadata" src={source}>Audio preview is unavailable.</audio>;
  if (source && type === 'video') return <video controls preload="metadata" src={source}>Video preview is unavailable.</video>;
  if (source && (type === 'image' || thumbnail)) {
    return <DocumentPreviewImage document={document} source={source} className={imageClassName} retry={thumbnail && type !== 'image'} />;
  }
  if (source) return <iframe title={`File preview for ${filename}`} src={source} className={iframeClassName} />;
  return (
    <div className={thumbnail ? 'document-explorer-preview__fallback' : 'document-file-viewer__unavailable'}>
      <DocumentFileTypeIcon file={document} aria-hidden="true" />
      {!thumbnail && <strong>No visual preview</strong>}
      <span>{documentTypeLabel(document)}{!thumbnail && ' files can still be downloaded.'}</span>
    </div>
  );
}
